import { Button, DatePicker, Form, Input, Select } from "antd";
import { Calendar } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import visaRequestServices from "../../../api/services/visaRequestServices";
import { VisaRequestStatusTable } from "../../../components";

const ProfileSearch = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [visaResult, setVisaResult] = useState<IVisaStatus[]>([]);

  const onFinish = async (values: any) => {
    const response = await visaRequestServices.checkVisaRequestStatus(
      values.passport_id,
      values.passport_type,
      values.fullname ? values.fullname : "",
      values.dob ? values.dob.format("YYYY-MM-DD") : ""
    );

    if (response.error) {
      console.error(response.error);
      return;
    }

    setVisaResult(response.data);
    const visaResultElement = document.getElementById("visa-result");
    visaResultElement?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div>
      <div className="w-full py-8 px-4 text-center text-white bg-gradient-to-b from-[#F00D0D] to-[#994F4F]">
        <h1 className="text-[32px] font-bold mb-2">
          {t("profile_search.title")}
        </h1>
        <p className="text-[22px] font-bold">
          {t("profile_search.description")}
        </p>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <Form form={form} onFinish={onFinish} layout="vertical">
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Form.Item
              label={
                <p className="text-primary-blue font-medium">
                  {t("registration_form.step3.passport_number")}
                </p>
              }
              name="passport_id"
              className="mb-0"
              rules={[
                {
                  required: true,
                  message: t("registration_form.empty_passport_number", {
                    ns: "message",
                  }),
                },
              ]}
            >
              <Input
                placeholder={t("registration_form.step3.passport_number_hint")}
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              label={
                <p className="text-primary-blue font-medium">
                  {t("payment_details.passport_type")}
                </p>
              }
              name="passport_type"
              className="mb-0"
              rules={[
                {
                  required: true,
                  message: t("registration_form.empty_passport_type", {
                    ns: "message",
                  }),
                },
              ]}
            >
              <Select placeholder={t("profile_search.passport_type_hint")}>
                <Select.Option value="Ordinary">
                  {t("registration_form.step3.ordinary_passport")}
                </Select.Option>
                <Select.Option value="Diplomatic">
                  {t("registration_form.step3.diplomatic_passport")}
                </Select.Option>
                <Select.Option value="Official">
                  {t("registration_form.step3.official_passport")}
                </Select.Option>
                <Select.Option value="Other">
                  {t("registration_form.step3.other_passport")}
                </Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              label={
                <p className="text-primary-blue font-medium">
                  {t("registration_form.step4.emergency_fullname")}
                </p>
              }
              name="fullname"
              className="mb-0"
            >
              <Input
                placeholder={t(
                  "registration_form.step4.emergency_fullname_hint"
                )}
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              label={
                <p className="text-primary-blue font-medium">
                  {t("registration_form.step2.date_of_birth")}
                </p>
              }
              name="dob"
              className="mb-0"
            >
              <DatePicker
                className="w-full"
                suffixIcon={<Calendar size={16} className="text-gray-400" />}
                format="DD/MM/YYYY"
                placeholder={t("registration_form.step2.date_of_birth_hint")}
              />
            </Form.Item>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <p className="text-blue-800 text-sm">
              {t("registration_form.step6.note")}:
            </p>
            <ul className="list-disc pl-5 text-sm text-primary-blue space-y-2">
              <li>{t("profile_search.note1")}</li>
              <li>{t("profile_search.note2")}</li>
              <li>{t("profile_search.note3")}</li>
            </ul>
          </div>

          <div className="flex justify-center mt-6">
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                className="bg-primary-lightyellow !hover:bg-primary-yellow text-primary-blue border-none px-12 py-6 h-auto rounded-full text-base font-medium"
              >
                {t("cta.search")}
              </Button>
            </Form.Item>
          </div>
        </Form>

        <div id="visa-result" className="mt-8">
          {visaResult.length > 0 && (
            <VisaRequestStatusTable data={visaResult} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileSearch;
